import { SalesMetricBasis, type SalesDocumentType } from '@plato/app-core/gql/schema-types';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useTranslation } from 'react-i18next';

export type ExplainableMetric = SalesMetricBasis | 'GROSS_MARGIN' | 'GROSS_MARGIN_PERCENTAGE' | 'UNITS';

export const explainableMetrics: ExplainableMetric[] = [
    ...Object.values(SalesMetricBasis),
    'GROSS_MARGIN',
    'GROSS_MARGIN_PERCENTAGE',
    'UNITS',
];

/** What a margin figure is calculated on. `ORDER_VALUE` and `QUOTE_VALUE` are one document's lines. */
export type MetricBase = SalesMetricBasis | 'ORDER_VALUE' | 'QUOTE_VALUE';

export type MetricCopy = {
    label: string;
    explanation: string;
};

/** Resolves a figure to its finished sentences (REQ-3777). */
export function useMetricCopy(): (metric: ExplainableMetric, base?: MetricBase) => MetricCopy {
    const { t } = useTranslation('shared');
    const { orderSumDocumentTypes } = useAppConfig();

    const getDocumentTypeLabel = (documentType: SalesDocumentType): string => {
        switch (documentType) {
            case 'ORDER':
                return t('document_type__ORDER');
            case 'CREDITNOTE':
                return t('document_type__CREDITNOTE');
            case 'QUOTE':
                return t('document_type__QUOTE');
            case 'RETURN':
                return t('document_type__RETURN');
            case 'CONTRACT':
                return t('document_type__CONTRACT');
            case 'DEBIT_MEMO_REQUEST':
                return t('document_type__DEBIT_MEMO_REQUEST');
            case 'CREDIT_MEMO_REQUEST':
                return t('document_type__CREDIT_MEMO_REQUEST');
        }
    };

    // A list of one reads like a warning about nothing.
    const documentTypes =
        orderSumDocumentTypes.length > 1
            ? orderSumDocumentTypes.map((type) => getDocumentTypeLabel(type)).join(', ')
            : undefined;

    const getBaseLabel = (base: MetricBase | undefined): string => {
        switch (base) {
            case SalesMetricBasis.BILLED:
                return t('base__billed');
            case SalesMetricBasis.BILLED_WO_CREDIT_NOTE:
                return t('base__billed_wo_credit_note');
            case SalesMetricBasis.NOT_BILLED:
                return t('base__not_billed');
            case SalesMetricBasis.ORDER_SUM:
                return t('base__order_sum');
            case 'ORDER_VALUE':
                return t('base__order_value');
            case 'QUOTE_VALUE':
                return t('base__quote_value');
            case undefined:
                return t('base__selected_figure');
        }
    };

    const getLabel = (metric: ExplainableMetric): string => {
        switch (metric) {
            case SalesMetricBasis.BILLED:
                return t('metric__billed__label');
            case SalesMetricBasis.BILLED_WO_CREDIT_NOTE:
                return t('metric__billed_wo_credit_note__label');
            case SalesMetricBasis.NOT_BILLED:
                return t('metric__not_billed__label');
            case SalesMetricBasis.ORDER_SUM:
                return t('metric__order_sum__label');
            case 'GROSS_MARGIN':
                return t('metric__gross_margin__label');
            case 'GROSS_MARGIN_PERCENTAGE':
                return t('metric__gross_margin_percentage__label');
            case 'UNITS':
                return t('metric__units__label');
        }
    };

    const getExplanation = (metric: ExplainableMetric, base: MetricBase | undefined): string => {
        const baseLabel = getBaseLabel(base);

        switch (metric) {
            case SalesMetricBasis.BILLED:
                return [
                    t('metric__billed__definition', { base: baseLabel }),
                    t('metric__billed__date_basis'),
                ].join(' ');
            case SalesMetricBasis.BILLED_WO_CREDIT_NOTE:
                return [
                    t('metric__billed_wo_credit_note__definition', { base: baseLabel }),
                    t('metric__billed_wo_credit_note__date_basis'),
                ].join(' ');
            case SalesMetricBasis.NOT_BILLED:
                return [
                    t('metric__not_billed__definition', { base: baseLabel }),
                    t('metric__not_billed__date_basis'),
                ].join(' ');
            case SalesMetricBasis.ORDER_SUM:
                return [
                    t('metric__order_sum__definition', { base: baseLabel }),
                    documentTypes && t('metric__order_sum__document_types', { documentTypes }),
                    t('metric__order_sum__date_basis'),
                ]
                    .filter(Boolean)
                    .join(' ');
            case 'GROSS_MARGIN':
                return t('metric__gross_margin__definition', { base: baseLabel });
            case 'GROSS_MARGIN_PERCENTAGE':
                return t('metric__gross_margin_percentage__definition', { base: baseLabel });
            case 'UNITS':
                return t('metric__units__definition');
        }
    };

    return (metric, base) => ({
        label: getLabel(metric),
        explanation: getExplanation(metric, base),
    });
}
